import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { IconButton, Button } from "@mui/material";
import { ModeEditOutlineTwoTone, InfoOutlined, Add } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";

import {
  useLazyGetEmailTemplateListQuery,
  useAddEmailTemplateMutation,
} from "store/services/emailTemplateApi";

import { useAppMeta, useTablePagination } from "hooks";
import { toast } from "react-toastify";

const EmailTemplateListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const breadcrumbs = [{ title: "Email Template" }];

  const broadcastListTabs = [
    { label: "Active", value: "Active" },
    { label: "InActive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "For",
      selector: (row) => row?.for ?? "N/A",
      cell: (row) => (
        <Link
          to={`/app/crm/email-template/${row._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.for}
        </Link>
      ),
    },
    {
      name: "Subject",
      selector: (row) => row?.subject,

    },
    {
      name: "Created Date",
      selector: (row) => row?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} dateOnly />,
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/email-template/${row._id}/detail`}>
            <IconButton color="primary" aria-label="Edit Broadcast">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/email-template/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Broadcast">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    listEmailTemplate,
    {
      data: emailTemplateRes,
      isLoading: loadingEmailTemplate,
      isFetching: fetchingEmailTemplate,
      error,
    },
  ] = useLazyGetEmailTemplateListQuery();

  const [onEmailPopulate] = useAddEmailTemplateMutation();

  const isBusy = loadingEmailTemplate || fetchingEmailTemplate;

  const onPopulate = async () => {
    try {
      await onEmailPopulate().unwrap();
      toast.success("Email templates added successfully!");
    } catch (error) {
      toast.error("Failed to populate email templates!");
    }
  };

  React.useEffect(() => {
    listEmailTemplate({
      page: currentPage,
      limit: perPage,
      active: activeTab === "Active",
    });
  }, [activeTab, currentPage, listEmailTemplate, perPage]);

  useAppMeta("Email Templates");

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={isBusy}
      headerActions={<PageActions onPopulate={onPopulate} />}
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={broadcastListTabs} activeTab={activeTab} />

        <AppTable
          columns={columns}
          data={emailTemplateRes?.data[0].data ?? []}
          progressPending={isBusy}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={emailTemplateRes?.data.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default EmailTemplateListScreen;

const PageActions = ({ onPopulate }) => {
  return (
    <Button onClick={onPopulate} startIcon={<Add />} variant="contained">
      Populate
    </Button>
  );
};
